import { type Profile, isProfile } from './profiles';

/**
 * The AI SDLC kit's own contract (`vivicta-ai-sdlc-kit-generic`): its roles,
 * gates and profile defaults live in `docs/sdlc/sdlc.json`, and the
 * repository's control dial in `.vivicta/control.json`. These readers mirror
 * the kit's `tools/sdlc_check.py`, so the portal shows what the kit enforces.
 */
export const KIT_SDLC_PATH = 'docs/sdlc/sdlc.json';
export const KIT_CONTROL_PATH = '.vivicta/control.json';
export const KIT_ASSURANCE_PATH = '.vivicta/assurance.json';

/** Gate numbers as the kit keys them: '1' to '7'. */
export type KitGate = string;

/** A0 human-only, A1 advisory, A2 preparation, A3 bounded execution. */
export const AUTONOMY_LEVELS = ['A0', 'A1', 'A2', 'A3', 'A4'] as const;
export type AutonomyLevel = (typeof AUTONOMY_LEVELS)[number];

export const isAutonomyLevel = (value: unknown): value is AutonomyLevel =>
  typeof value === 'string' && (AUTONOMY_LEVELS as readonly string[]).includes(value);

export type KitProfileDefaults = {
  autonomyCeilings: Record<string, AutonomyLevel>;
  escalationReviewers: string[];
  productionRelease: boolean;
};

export type KitSdlc = {
  /** Role keys in the order the kit lists them. */
  roles: string[];
  /** Role key → SSD title, e.g. outcome_owner → Product Owner. */
  roleTitles: Record<string, string>;
  phases: Record<string, { name: string; gate?: KitGate; activities: string[] }>;
  gates: Record<KitGate, { name: string; phase: number }>;
  decisionOwners: Record<KitGate, string[]>;
  concurrences: Record<KitGate, string[]>;
  profiles: Partial<Record<Profile, KitProfileDefaults>>;
};

export type KitControl = {
  initiative: { id: string; name: string };
  profile?: Profile;
  blueprint?: string;
  /** Role key → '@org/team' or '@login'. */
  roles: Record<string, string>;
  autonomy: Record<string, AutonomyLevel>;
  gateApprovers: Record<KitGate, string[]>;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const strings = (value: unknown) =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

const stringMap = (value: unknown) =>
  Object.fromEntries(
    Object.entries(isObject(value) ? value : {}).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string',
    ),
  );

const listMap = (value: unknown) =>
  Object.fromEntries(
    Object.entries(isObject(value) ? value : {}).map(([key, list]) => [key, strings(list)]),
  );

const levelMap = (value: unknown) =>
  Object.fromEntries(
    Object.entries(isObject(value) ? value : {}).filter(
      (entry): entry is [string, AutonomyLevel] => isAutonomyLevel(entry[1]),
    ),
  );

/** Reads `docs/sdlc/sdlc.json`; throws when it isn't the kit's format. */
export function parseKitSdlc(value: unknown): KitSdlc {
  if (!isObject(value) || !isObject(value.gates) || !isObject(value.gate_decision_owners)) {
    throw new Error(`${KIT_SDLC_PATH} has no gates or gate_decision_owners.`);
  }
  const phases: KitSdlc['phases'] = {};
  for (const [key, phase] of Object.entries(isObject(value.phases) ? value.phases : {})) {
    if (!isObject(phase) || typeof phase.name !== 'string') continue;
    phases[key] = {
      name: phase.name,
      ...(typeof phase.gate === 'string' && { gate: phase.gate }),
      activities: strings(phase.activities),
    };
  }
  const gates: KitSdlc['gates'] = {};
  for (const [key, gate] of Object.entries(value.gates)) {
    if (isObject(gate) && typeof gate.name === 'string' && typeof gate.phase === 'number') {
      gates[key] = { name: gate.name, phase: gate.phase };
    }
  }
  const profiles: KitSdlc['profiles'] = {};
  for (const [key, profile] of Object.entries(isObject(value.profiles) ? value.profiles : {})) {
    if (!isProfile(key) || !isObject(profile)) continue;
    profiles[key] = {
      autonomyCeilings: levelMap(profile.autonomy_ceilings),
      escalationReviewers: strings(profile.escalation_reviewers),
      productionRelease: profile.production_release === true,
    };
  }
  return {
    roles: isObject(value.roles) ? Object.keys(value.roles) : strings(value.roles),
    roleTitles: stringMap(value.role_mapping),
    phases,
    gates,
    decisionOwners: listMap(value.gate_decision_owners),
    concurrences: listMap(value.gate_concurrences),
    profiles,
  };
}

/** Reads `.vivicta/control.json`; throws when it has no initiative or roles. */
export function parseKitControl(value: unknown): KitControl {
  if (!isObject(value) || !isObject(value.initiative) || !isObject(value.roles)) {
    throw new Error(`${KIT_CONTROL_PATH} has no initiative or roles.`);
  }
  const { id, name } = value.initiative;
  return {
    initiative: { id: typeof id === 'string' ? id : '', name: typeof name === 'string' ? name : '' },
    ...(isProfile(value.profile) && { profile: value.profile }),
    ...(typeof value.blueprint === 'string' && { blueprint: value.blueprint }),
    roles: stringMap(value.roles),
    autonomy: levelMap(value.autonomy),
    gateApprovers: listMap(value.gate_approvers),
  };
}

/**
 * The template's placeholder control file: no real initiative or role
 * owners adopted yet (the kit's `unconfigured()`).
 */
export const isKitUnconfigured = (control: KitControl) =>
  control.initiative.id === 'INI-0000' ||
  Object.values(control.roles).some(owner => owner.startsWith('@your-org/'));

/** Who decides a gate. At P2 and P3 the Security Governance Office decides Gate 3. */
export function kitGateOwners(sdlc: KitSdlc, control: KitControl, gate: KitGate): string[] {
  if (gate === '3' && (control.profile === 'P2' || control.profile === 'P3')) {
    return ['security_governance'];
  }
  return sdlc.decisionOwners[gate] ?? [];
}

/**
 * Every role that must approve a gate PR, each by a different person: the
 * control file's `gate_approvers` override, else the decision owners plus
 * the concurrences. `decides` is who may post the `/gate` decision.
 */
export function kitGateApprovals(
  sdlc: KitSdlc,
  control: KitControl,
  gate: KitGate,
): { decides: string[]; concurs: string[] } {
  const decides = kitGateOwners(sdlc, control, gate);
  const approvers = control.gateApprovers[gate]?.length
    ? control.gateApprovers[gate]
    : [...decides, ...(sdlc.concurrences[gate] ?? [])];
  return { decides, concurs: approvers.filter(role => !decides.includes(role)) };
}

/** The autonomy ceiling for an activity: the control file's value, else the profile's, else A0. */
export const kitAutonomyCeiling = (
  sdlc: KitSdlc,
  control: KitControl,
  activity: string,
): AutonomyLevel =>
  control.autonomy[activity] ??
  (control.profile && sdlc.profiles[control.profile]?.autonomyCeilings[activity]) ??
  'A0';

/** "Product Owner", or the key in words when the kit gives no title. */
export const kitRoleTitle = (sdlc: KitSdlc, role: string) =>
  sdlc.roleTitles[role] ?? role.replaceAll('_', ' ').replace(/^./, c => c.toUpperCase());
